import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    AlertTriangle, Loader2, TrendingDown, TrendingUp, Zap, Timer,
    ShieldAlert, ShieldCheck,
} from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { ROLES } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

function pct(n) {
    if (n === null || n === undefined) return "—";
    return `${Number(n).toFixed(2)}%`;
}
function money(n, cur = "USD") {
    if (n === null || n === undefined) return "—";
    return Number(n).toLocaleString("es-MX", {
        style: "currency", currency: cur, maximumFractionDigits: 0,
    });
}
function delta(v) {
    if (v === null || v === undefined) return "—";
    const n = Number(v);
    const sign = n >= 0 ? "+" : "";
    return `${sign}${money(n)}`;
}
function deltaColor(v) {
    return Number(v || 0) >= 0
        ? "text-[color:var(--brand-exito)]"
        : "text-[color:var(--brand-coral)]";
}

const PRESETS = [
    { label: "Moderado", sofr: 100, haircut: 5, dpd: 3, conc: 2,
      testid: "preset-moderado" },
    { label: "Adverso", sofr: 200, haircut: 10, dpd: 8, conc: 5,
      testid: "preset-adverso" },
    { label: "Severamente adverso", sofr: 400, haircut: 20, dpd: 15, conc: 10,
      testid: "preset-severo" },
];

export default function StressTestTab({ facility }) {
    const { hasRole } = useAuth();
    const [history, setHistory] = useState([]);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        sofr_bump_bps: "200",
        haircut_bump_pct: "10",
        dpd_shock_pct: "8",
        concentracion_bump_pct: "5",
    });

    const canRun = hasRole([
        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.TESORERIA, ROLES.OPERACIONES,
        ROLES.ADMIN_TI, ROLES.RIESGOS,
    ]);

    const loadHistory = async () => {
        try {
            const { data } = await api.get(
                `/facilities/${facility.id}/stress-tests/historico`,
            );
            setHistory(data);
            if (data.length > 0 && !result) setResult(data[0]);
        } catch { /* silencio */ }
        finally { setLoading(false); }
    };
    useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [facility.id]);

    const applyPreset = (p) => {
        setForm({
            sofr_bump_bps: String(p.sofr),
            haircut_bump_pct: String(p.haircut),
            dpd_shock_pct: String(p.dpd),
            concentracion_bump_pct: String(p.conc),
        });
    };

    const run = async () => {
        setBusy(true);
        try {
            const payload = {
                sofr_bump_bps: Number(form.sofr_bump_bps) || 0,
                haircut_bump_pct: Number(form.haircut_bump_pct) || 0,
                dpd_shock_pct: Number(form.dpd_shock_pct) || 0,
                concentracion_bump_pct: Number(form.concentracion_bump_pct) || 0,
            };
            const { data } = await api.post(
                `/facilities/${facility.id}/stress-test`, payload,
            );
            setResult(data);
            toast.success("Stress test ejecutado");
            loadHistory();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="tab-stress-content">
            <div>
                <h3 className="font-display text-2xl flex items-center gap-2">
                    <Zap className="h-5 w-5 text-[color:var(--brand-dark-gold)]" />
                    Stress test waterfall
                </h3>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                    Aplica shocks (SOFR bump, haircut, DPD shift, concentración) sobre
                    el corte actual y recalcula Borrowing Base, cushion, covenants
                    breach y meses de supervivencia con el cushion stressed.
                </p>
            </div>

            {canRun && (
                <div className="rounded-md border border-border bg-card p-5" data-testid="stress-form">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Escenarios predefinidos:
                        </span>
                        {PRESETS.map((p) => (
                            <Button key={p.label} size="sm" variant="outline"
                                onClick={() => applyPreset(p)}
                                data-testid={p.testid}>
                                {p.label}
                            </Button>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                            <Label className="text-xs">SOFR bump (bps)</Label>
                            <Input type="number" value={form.sofr_bump_bps}
                                onChange={(e) => setForm({ ...form, sofr_bump_bps: e.target.value })}
                                className="mt-1" data-testid="stress-sofr-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Haircut extra (pp)</Label>
                            <Input type="number" value={form.haircut_bump_pct}
                                onChange={(e) => setForm({ ...form, haircut_bump_pct: e.target.value })}
                                className="mt-1" data-testid="stress-haircut-input" />
                        </div>
                        <div>
                            <Label className="text-xs">DPD shift a &gt;90d (%)</Label>
                            <Input type="number" value={form.dpd_shock_pct}
                                onChange={(e) => setForm({ ...form, dpd_shock_pct: e.target.value })}
                                className="mt-1" data-testid="stress-dpd-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Concentración top-10 (pp)</Label>
                            <Input type="number" value={form.concentracion_bump_pct}
                                onChange={(e) => setForm({ ...form, concentracion_bump_pct: e.target.value })}
                                className="mt-1" data-testid="stress-conc-input" />
                        </div>
                    </div>
                    <Button className="mt-4" onClick={run} disabled={busy}
                        data-testid="stress-run-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              : <TrendingDown className="h-4 w-4 mr-1" />}
                        Correr stress test
                    </Button>
                </div>
            )}

            {result && (
                <div className="space-y-4" data-testid="stress-result-block">
                    <h4 className="font-display text-lg">Resultado más reciente</h4>
                    {/* Base vs Stressed */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="rounded-md border border-border bg-card p-4">
                            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                                Base
                            </div>
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">BB</span>
                                    <span className="tabular font-medium">
                                        {money(result.base?.bb)}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Cushion</span>
                                    <span className="tabular font-medium">
                                        {money(result.base?.cushion)}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Tasa</span>
                                    <span className="tabular">{pct(result.base?.tasa_pct)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Interés mensual</span>
                                    <span className="tabular">{money(result.base?.interes_mensual)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-md border-2 border-[color:var(--brand-coral)]/50 bg-[color:var(--brand-coral)]/5 p-4">
                            <div className="text-[11px] uppercase tracking-widest text-[color:var(--brand-coral)] font-semibold mb-2 flex items-center gap-2">
                                <AlertTriangle className="h-3 w-3" /> Stressed
                            </div>
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">BB</span>
                                    <div className="text-right">
                                        <span className="tabular font-medium">
                                            {money(result.resultado?.bb_stressed)}
                                        </span>
                                        <div className={`text-[10px] tabular ${deltaColor(result.resultado?.delta_bb)}`}>
                                            {delta(result.resultado?.delta_bb)}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Cushion</span>
                                    <div className="text-right">
                                        <span className="tabular font-medium">
                                            {money(result.resultado?.cushion_stressed)}
                                        </span>
                                        <div className={`text-[10px] tabular ${deltaColor(result.resultado?.delta_cushion)}`}>
                                            {delta(result.resultado?.delta_cushion)}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Tasa</span>
                                    <span className="tabular">{pct(result.resultado?.tasa_stressed_pct)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Interés mensual</span>
                                    <span className="tabular">{money(result.resultado?.interes_mensual_stressed)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Supervivencia + breaches */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="kpi-card">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                <Timer className="h-3 w-3" /> Meses de supervivencia
                            </div>
                            <div className={`font-display text-4xl mt-2 ${result.resultado?.supervivencia_meses > 6 ? "text-[color:var(--brand-exito)]" : result.resultado?.supervivencia_meses > 3 ? "text-[color:var(--brand-dark-gold)]" : "text-[color:var(--brand-coral)]"}`}
                                data-testid="stress-supervivencia">
                                {result.resultado?.supervivencia_meses}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1">
                                Cushion stressed / interés mensual stressed
                            </div>
                        </div>
                        <div className="kpi-card">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                                <ShieldAlert className="h-3 w-3" /> Covenants breached
                            </div>
                            <div className={`font-display text-4xl mt-2 ${result.resultado?.breaches_stressed === 0 ? "text-[color:var(--brand-exito)]" : "text-[color:var(--brand-coral)]"}`}
                                data-testid="stress-breaches">
                                {result.resultado?.breaches_stressed}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1">
                                de {result.resultado?.covenants_stressed?.length || 0} covenants totales
                            </div>
                        </div>
                    </div>

                    {/* Covenants breakdown */}
                    <div className="rounded-md border border-border bg-card overflow-hidden">
                        <table className="trf-table" data-testid="stress-covenants-table">
                            <thead>
                                <tr>
                                    <th>Covenant</th>
                                    <th>Descripción</th>
                                    <th>Fórmula</th>
                                    <th>Resultado stressed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(result.resultado?.covenants_stressed || []).map((c, i) => (
                                    <tr key={i} data-testid={`stress-cov-${c.codigo}`}>
                                        <td className="font-medium text-xs">{c.codigo}</td>
                                        <td className="text-xs text-muted-foreground max-w-xs truncate">
                                            {c.descripcion}
                                        </td>
                                        <td className="text-[10px] tabular text-muted-foreground">
                                            {c.formula}
                                        </td>
                                        <td>
                                            {c.resultado_stressed === "cumple" ? (
                                                <span className="badge-exito">
                                                    <ShieldCheck className="h-3 w-3" /> Cumple
                                                </span>
                                            ) : (
                                                <span className="badge-error">
                                                    <ShieldAlert className="h-3 w-3" /> Breach
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="text-[10px] text-muted-foreground border-t border-border/60 pt-3">
                        Escenario: SOFR +{result.escenario?.sofr_bump_bps}bps
                        {" · "}Haircut +{result.escenario?.haircut_bump_pct}pp
                        {" · "}DPD +{result.escenario?.dpd_shock_pct}%
                        {" · "}Conc +{result.escenario?.concentracion_bump_pct}pp
                        {" · "}Ejecutado {result.ejecutado_at?.slice(0, 16)?.replace("T", " ")}
                    </div>
                </div>
            )}

            {/* Historial */}
            {history.length > 1 && (
                <div className="rounded-md border border-border bg-card p-5">
                    <h4 className="font-display text-lg mb-3">Historial</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {history.map((h) => (
                            <div key={h.id}
                                className={`flex items-center gap-3 py-2 border-b border-border/40 cursor-pointer hover:bg-muted/50 px-2 rounded ${result?.id === h.id ? "bg-muted/40" : ""}`}
                                onClick={() => setResult(h)}
                                data-testid={`stress-history-${h.id}`}>
                                <div className="flex-1">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground tabular">
                                        {h.ejecutado_at?.slice(0, 16)?.replace("T", " ")}
                                    </div>
                                    <div className="text-xs mt-0.5">
                                        SOFR +{h.escenario?.sofr_bump_bps}bps ·
                                        Haircut +{h.escenario?.haircut_bump_pct}pp ·
                                        DPD +{h.escenario?.dpd_shock_pct}%
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className={`text-xs tabular ${deltaColor(h.resultado?.delta_cushion)}`}>
                                        {delta(h.resultado?.delta_cushion)}
                                    </span>
                                    <div className="text-[10px] text-muted-foreground">
                                        {h.resultado?.breaches_stressed} breaches
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {loading && !result && (
                <div className="p-8 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Cargando historial…
                </div>
            )}
        </div>
    );
}
